import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// TYPE-ONLY, and it must stay that way. `subscription.repository.ts` imports THIS
// file as a VALUE (its root DI factory picks an implementation), so a value import
// back would close a runtime cycle: this module would evaluate while that one is
// still initialising. `implements` + `import type` is erased at compile time, so
// the contract is still enforced by the compiler with no runtime edge at all.
import type { CheckoutRequest, SubscriptionRepository } from '@domains/subscription/domain/interfaces/subscription.interface';
import {
  CardUpdateCheckout,
  ChargeVerification,
  CheckoutOutcome,
  SubscriptionCard,
  SubscriptionInvoice,
  SubscriptionCatalog,
  SubscriptionQuote,
  SubscriptionSnapshot,
} from '@domains/subscription/domain/models/subscription.model';
import { SubscriptionApiClient } from '../api/subscription.api-client';
import { PaymentMethodRequestDto } from '../api/dtos/requests/payment-method-dto-request';
import { SubscribeRequestDto } from '../api/dtos/requests/subscribe-dto-request';
import {
  autoRenewToSnapshot,
  cancelToSnapshot,
  toCardUpdateCheckout,
  toCards,
  toCheckoutOutcome,
  toInvoices,
  toCatalog,
  toQuote,
  toSnapshot,
  toToolsCount,
  trialToSnapshot,
} from '../mappers/subscription.mapper';

/**
 * SubscriptionHttpRepository — the REAL `/api/v3/subscription/*` binding. Pure
 * transport + mapping: it calls {@link SubscriptionApiClient}, runs the response
 * through the mapper, and hands a DOMAIN value back. It holds no state, writes
 * no storage and swallows no error.
 *
 * That last point is deliberate and is the main behavioural change from the
 * earlier version of this file. It used to `catchError` every mutation into a
 * LOCAL optimistic state machine — a failed `/subscribe` wrote `premium_active`
 * to localStorage, which then leaked into the Profile tier row and every premium
 * badge in the app. A payment that did not happen must never look like one, so
 * failures now propagate; `SubscriptionStore` decides what to show (and keeps its
 * read cache for the offline / cold-start case).
 */
@Service()
export class SubscriptionHttpRepository implements SubscriptionRepository {
  private readonly api = inject(SubscriptionApiClient);

  getSnapshot(): Observable<SubscriptionSnapshot> {
    return this.api.get().pipe(map(toSnapshot));
  }

  plans(): Observable<SubscriptionCatalog> {
    return this.api.plans().pipe(map(toCatalog));
  }

  startTrial(): Observable<Partial<SubscriptionSnapshot>> {
    return this.api.startTrial().pipe(map(trialToSnapshot));
  }

  cancel(reason?: string | null): Observable<Partial<SubscriptionSnapshot>> {
    const trimmed = reason?.trim();
    return this.api.cancel(trimmed ? { reason: trimmed } : {}).pipe(map(cancelToSnapshot));
  }

  subscribe(request: CheckoutRequest): Observable<CheckoutOutcome> {
    return this.api.subscribe(toRequestDto(request)).pipe(map(toCheckoutOutcome));
  }

  reactivate(request: CheckoutRequest): Observable<CheckoutOutcome> {
    return this.api.reactivate(toRequestDto(request)).pipe(map(toCheckoutOutcome));
  }

  quote(request: CheckoutRequest): Observable<SubscriptionQuote> {
    // `0` as the fallback subtotal: the repository has no catalog, so the store
    // completes the figure from the plan it resolved.
    return this.api.quote(toRequestDto(request)).pipe(map((res) => toQuote(res, request.planId, request.cycle, 0)));
  }

  setAutoRenew(on: boolean): Observable<Partial<SubscriptionSnapshot>> {
    // `enabled` — the field the API actually validates (see AutoRenewRequestDto).
    return this.api.autoRenew({ enabled: on }).pipe(map((res) => autoRenewToSnapshot(res, on)));
  }

  updatePaymentMethod(redirectUrl: string | null): Observable<CardUpdateCheckout> {
    // `payment_method: 'card'` keeps the pre-R3 body valid; the server now builds
    // the captured hosted charge itself.
    const body: PaymentMethodRequestDto = { payment_method: 'card' };
    if (redirectUrl) body.redirect_url = redirectUrl;
    return this.api.paymentMethod(body).pipe(map(toCardUpdateCheckout));
  }

  verifyCharge(chargeId: string): Observable<ChargeVerification> {
    return this.api.verify({ charge_id: chargeId }).pipe(
      map((res) => ({
        mandateCaptured: typeof res?.mandate_captured === 'boolean' ? res.mandate_captured : null,
        reason: res?.reason?.trim() || null,
      })),
    );
  }

  invoices(): Observable<SubscriptionInvoice[]> {
    return this.api.invoices().pipe(map(toInvoices));
  }

  cards(): Observable<SubscriptionCard[]> {
    return this.api.cards().pipe(map(toCards));
  }

  toolsCount(): Observable<number | null> {
    return this.api.tools().pipe(map(toToolsCount));
  }
}

/** Domain {@link CheckoutRequest} → the shared subscribe/reactivate/quote body. */
function toRequestDto(request: CheckoutRequest): SubscribeRequestDto {
  const body: SubscribeRequestDto = { plan_id: request.planId, cycle: request.cycle };
  const promo = request.promoCode?.trim();
  if (promo) body.promo_code = promo;
  if (request.useWallet) body.use_wallet = true;
  if (request.cardId) body.card_id = request.cardId;
  if (request.paymentMethod) body.payment_method = request.paymentMethod;
  if (request.redirectUrl) body.redirect_url = request.redirectUrl;
  return body;
}
